import { Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { StanLoader } from '@/components/core-components/StanLoader/StanLoader';
import { stanNotify } from '@/lib/stanNotify';
import { createSetupIntent } from '@/lib/api/stripe';
import { useStripe } from '@/providers/StripeProvider';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { getUUID } from '@/lib/helpers';
import { useGlobalStore } from '@/stores';

interface StripePaymentInputProps {
    showWallets?: boolean;
}

export interface StripePaymentInputRef {
    createPaymentMethod: (stripeCustomerId: string, fullName: string, email: string) => Promise<string | null>;
}

export const StripePaymentInput = ({
    showWallets = false,
    ref,
}: StripePaymentInputProps & { ref?: Ref<StripePaymentInputRef> }) => {
    const stripe = useStripe();
    const { getToken, isReady: recaptchaReady } = useRecaptcha();
    const [stripeReady, setStripeReady] = useState(false);
    const paymentElementId = useRef(`payment-element-${getUUID()}`);
    const elementsRef = useRef<any>(null);
    const darkMode = useGlobalStore(state => state.darkMode);

    useEffect(() => {
        if (stripe) {
            createPaymentElement();
        }
    }, [stripe]);

    const getElementAppearance = () => {
        return {
            theme: darkMode ? 'night' : 'stripe',
            labels: 'floating',
            variables: {
                fontSizeBase: '14px',
                fontFamily: '"Inter", sans-serif',
                focusBoxShadow: 'transparent',
                borderRadius: '12px',
                spacingUnit: '3px',
                fontSmooth: 'always',
            },
        };
    };

    const createPaymentElement = () => {
        if (!stripe) return;

        const elementsOptions: any = {
            mode: 'setup',
            currency: 'usd',
            paymentMethodCreation: 'manual',
            paymentMethodTypes: ['card'],
            loader: 'auto',
            setupFutureUsage: 'off_session',
            appearance: getElementAppearance(),
        };

        elementsRef.current = stripe.elements(elementsOptions);

        const paymentElement = elementsRef.current.create('payment', {
            layout: 'tabs',
            wallets: {
                applePay: showWallets ? 'auto' : 'never',
                googlePay: showWallets ? 'auto' : 'never',
                link: 'never',
            },
            fields: {
                billingDetails: {
                    address: 'if_required',
                    name: 'never',
                    email: 'never',
                },
            },
            terms: { card: 'never', applePay: 'never', googlePay: 'never' },
        });

        paymentElement.mount(`#${paymentElementId.current}`);
        paymentElement.on('ready', () => {
            setStripeReady(true);
        });
    };

    const createPaymentMethod = async (
        stripeCustomerId: string,
        fullName: string,
        email: string
    ): Promise<string | null> => {
        if (!stripe) {
            stanNotify({
                type: 'error',
                title: 'Stripe not loaded',
                text: 'Please wait for Stripe to load and try again.',
            });
            return null;
        }

        let result: any = {};

        // Handles 3DS / Apple pay - Needs to be done first, since apple pay must be 'near' the click event
        result = await elementsRef.current.submit();

        // Error likely means they exited their google pay / apple pay setup
        if (!result.error) {
            if (!recaptchaReady) {
                stanNotify({
                    type: 'error',
                    title: 'Something went wrong',
                    text: 'We were unable to validate your payment method. Please try again in a few seconds.',
                });
                return null;
            }

            try {
                const token = await getToken('CreateSetupIntent');
                const secret = await createSetupIntent(stripeCustomerId, token);

                result = await stripe.confirmSetup({
                    elements: elementsRef.current,
                    clientSecret: secret,
                    confirmParams: {
                        return_url: window.location.href,
                        payment_method_data: {
                            billing_details: {
                                name: fullName,
                                email,
                            },
                        },
                    },
                    redirect: 'if_required',
                });
            } catch (error) {
                console.error('reCAPTCHA or setup intent error:', error);
                result.error = error;
            }
        }

        if (result.error) {
            stanNotify({
                type: 'error',
                title: 'There was an issue',
                text: 'We were unable to validate your payment method. Please try another card.',
            });
            console.error(result.error);
            return null;
        }

        return result.setupIntent.payment_method;
    };

    useImperativeHandle(
        ref,
        () =>
            ({
                createPaymentMethod,
            }) as StripePaymentInputRef
    );

    return (
        <div id='#payment-form' className='h-30'>
            <div className='my-2'>
                <div id={paymentElementId.current} className={stripeReady ? 'block' : 'hidden'} />
                {!stripeReady && (
                    <div className='flex h-30 items-center justify-center'>
                        <StanLoader
                            className='flex items-center'
                            animationDuration={1000}
                            dotSize={14}
                            dotsNum={3}
                            color='var(--bg-color-primary-brand-main)'
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

StripePaymentInput.displayName = 'StripePaymentInput';
